import React, { useMemo } from 'react'
import {
  clientPickerLabel,
  projectPickerLabel,
  useClientProjectContext,
} from '../../../context/ClientProjectContext'
import { useClientContext } from '../../../context/ClientContext'
import { useProjectContext } from '../../../context/ProjectContext'
import { useAuth } from '../../../context/AuthContext'
import { ThickSelectPicker } from '../../ui_reusable/StandardSelectPickers'

interface SelectPickerItem {
  value: string | number | null
  label: string
}

const ClientProjectPicker = ({
  disabled = false,
  style,
  disabledItemValues = [],
  interceptClickWaitOnConfirm = async () => true,
}: {
  disabled?: boolean
  style?: React.CSSProperties
  disabledItemValues?: (string | null)[]
  interceptClickWaitOnConfirm?: (
    value: string | number | null,
    label: string,
    selectedValue: string | number | null,
    data: SelectPickerItem[],
  ) => Promise<boolean>
}) => {
  const { clientProjectData } = useClientProjectContext()
  const { selectedClient, setSelectedClient } = useClientContext()
  const { selectedProject, setSelectedProject } = useProjectContext()
  const { isAdmin, isClientManager } = useAuth()

  // Determine if user has admin privileges
  const hasAdminPrivileges = isAdmin || isClientManager

  // Format data based on user role
  const pickerData = useMemo(() => {
    if (hasAdminPrivileges) {
      // Admin/Manager view - show full client-project combination
      return [
        {
          key: 0,
          label: 'Select all',
          value: null,
        },
        ...clientProjectData.map((elem, i) => ({
          key: i + 1,
          label: `${clientPickerLabel(elem.client.name, elem.client.is_active)} - ${projectPickerLabel(elem.project.name, elem.project.status)}`,
          value: `${elem.client.id}-${elem.project.id}`,
        })),
      ]
    } else {
      // Regular user view - only show project names for their client
      return clientProjectData
        .filter((elem) => elem.client.id === selectedClient?.id)
        .map((elem, i) => ({
          key: i + 1,
          label: projectPickerLabel(elem.project.name, elem.project.status),
          value: `${elem.client.id}-${elem.project.id}`,
        }))
    }
  }, [clientProjectData, selectedClient?.id, hasAdminPrivileges])

  // Handler for selection changes
  const handleSelectClientProject = (value: string | number | null) => {
    if (!value) {
      // Only reset project for regular users, both for admins
      if (hasAdminPrivileges) {
        setSelectedClient(null)
        setSelectedProject(null)
      } else {
        setSelectedProject(null)
      }

      return
    }
    const stringValue = String(value)

    const [clientId, projectId] = stringValue.split('-')
    const selectedClientProject = clientProjectData.find(
      (elem) => Number(elem.client.id) === Number(clientId),
    )
    const selectedProjectData = clientProjectData.find(
      (elem) => Number(elem.project.id) === Number(projectId),
    )

    if (selectedClientProject && selectedProjectData) {
      if (hasAdminPrivileges) {
        // Admin/Manager can change both client and project
        setSelectedClient(selectedClientProject.client)
        setSelectedProject(selectedProjectData.project)
      } else {
        // Regular users can only change project
        setSelectedProject(selectedProjectData.project)
      }
    }
  }

  return (
    <ThickSelectPicker
      disabled={disabled}
      placeholder={
        hasAdminPrivileges ? 'Select client & project' : 'Select project'
      }
      data={pickerData}
      value={
        selectedClient && selectedProject
          ? `${selectedClient?.id}-${selectedProject?.id}`
          : null
      }
      onChange={(value) => handleSelectClientProject(value)}
      style={{ width: 250, minWidth: '30%', flexShrink: 0, ...style }}
      disabledItemValues={disabledItemValues} // Add disabled items support
      interceptClickWaitOnConfirm={interceptClickWaitOnConfirm}
    />
  )
}

export default ClientProjectPicker
